// ── TRACKING TIMELINE — the physical-stage rail on the item page ─────────────
// Tracker rebuild item 5 (Cimon's 2026-07-28 spec). One row per physical
// stage, each with ITS OWN editable date — the item carries its whole
// journey; nothing is overwritten as it advances. Renders on the Status &
// Tracking tab for items at PO Issued or beyond.
//
// Interaction contract:
//   • The stage Dropdown sets the CURRENT stage (physical stages only —
//     financial statuses stay cascade-owned; this control never offers
//     Client Approved / Deposit Paid). Picking a stage stamps that
//     stage's date with today only when the date is empty.
//   • Entering a date on a stage AHEAD of the current one auto-advances
//     the status to that stage (forward only — Cimon: "once we are able
//     to add the date received, it will update in the app as we go").
//     Entering a date on a past stage just records the date.
//   • Direct-to-site items skip At Receiver by picking Delivered — the
//     receiver row simply stays dateless.
//   • Shipped carries tracking number + carrier; Installed carries the
//     planned date alongside the actual.
//
// Pure presentation + onPatch — parent (ItemForm) owns form state and
// the save flow; every field rides the normal item save + change_history
// audit. No Supabase, no context.

import React from 'react';
import { Dropdown, Inp, Lbl } from './ui';
import { STATUSES, statusColor, estimatedShipDate, itemLocationState, isABD, isNonProcured, plannedInstallDisplay, sortInstallEvents, installEventIsPast, installPlanOverdue } from '../utils';

// Stage → date-field registry. Order matters (mirrors STATUSES).
export const PHYSICAL_STAGES = [
  { status: 'Ordered',     dateField: 'orderPlacedDate', dateLabel: 'Order date',
    sub: 'vendor paid / order confirmed' },
  { status: 'Shipped',     dateField: 'shippedDate',     dateLabel: 'Ship date',
    sub: 'carrier has it' },
  { status: 'At Receiver', dateField: 'receivedDate',    dateLabel: 'Received date',
    sub: 'receiver / workroom / fabricator' },
  { status: 'Delivered',   dateField: 'deliveredDate',   dateLabel: 'Delivery date',
    sub: 'on site' },
  { status: 'Installed',   dateField: 'installDate',     dateLabel: 'Install date',
    sub: 'installation complete' },
];

export const LOCATION_WORDS = {
  production:       'In production',
  transit:          'In transit',
  receiver:         'At receiver',
  installScheduled: 'On site — awaiting install',
  installed:        'Installed',
};

var PO_ISSUED_IDX = STATUSES.indexOf('PO Issued');

// The stage the timeline treats as current. Complete sits past Installed.
function currentStageIdx(status) {
  var i = STATUSES.indexOf(status);
  if (status === 'Complete') return PHYSICAL_STAGES.length; // past the last row
  for (var s = PHYSICAL_STAGES.length - 1; s >= 0; s--) {
    if (STATUSES.indexOf(PHYSICAL_STAGES[s].status) === i) return s;
  }
  return -1; // PO Issued or earlier
}

export default function TrackingTimeline(p) {
  var timelineToday = new Date().toISOString().slice(0, 10);
  var installEvents = sortInstallEvents((p.project && Array.isArray(p.project.installEvents)) ? p.project.installEvents.filter(function(e) { return e && e.id; }) : [], timelineToday);
  var item = p.item || {};
  var onPatch = p.onPatch || function() {};
  var locked = !!p.locked;
  var vendors = p.vendors || [];
  // Receiver choices — same taxonomy as the Ships-to select on the item
  // form (Cimon 2026-07-28: "at receiver could be a number of things —
  // art receiver, fabricator for drapery…"). Workrooms suffix their type
  // so a fabric-at-fabricator stop reads unambiguously.
  var receiverVendors = vendors.filter(function(v) {
    return v && (v.vendorType === 'Receiver' || v.vendorType === 'Workroom');
  });
  var shipToVendor = item.shipToVendorId != null
    ? vendors.find(function(v) { return v && String(v.id) === String(item.shipToVendorId); }) || null
    : null;

  var statusIdx = STATUSES.indexOf(item.status);
  // Below PO Issued there is nothing physical to track yet — EXCEPT ABD
  // items (2026-07-28, the dd6590d5 stuck-item case): pre-existing goods
  // bought by the prior owner never ride proposals/deposits/POs, so they
  // can never reach PO Issued — yet they absolutely get received,
  // delivered, and installed. ABD items get the timeline at any status;
  // the money stages simply don't apply to them.
  // ...and Owner Furnished / By Others items (2026-07-29): the client or
  // the GC buys them, so they never reach PO Issued — but they absolutely
  // get received, delivered, and installed. Classification decides who
  // BUYS; physical tracking is for everything that physically exists.
  var freeTrack = isABD(item) || isNonProcured(item.procurementClass);
  if ((statusIdx < PO_ISSUED_IDX && !freeTrack) || item.status === 'Cancelled') return null;

  var curStage = currentStageIdx(item.status);
  var estIso = estimatedShipDate(item);
  var estPretty = null;
  if (estIso) {
    try {
      var d = new Date(estIso + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        estPretty = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch (e) { /* unparseable lead time — show nothing, never guess */ }
  }
  var locState = itemLocationState(item);
  var location = LOCATION_WORDS[locState] || '';
  // Name the receiver in the location answer when we know it.
  if (locState === 'receiver' && shipToVendor) {
    location = 'At receiver — ' + shipToVendor.name;
  }
  // OF/BO/ABD with no physical stage yet: 'In production' would be a
  // guess (nobody is producing the client's own settee) — show a dash
  // until a location is set.
  if (freeTrack && curStage < 0 && item.status !== 'Complete') {
    location = '\u2014';
  }

  // Stage pick: set status; stamp the stage's date only when empty.
  function pickStage(status) {
    var patch = { status: status };
    var stage = PHYSICAL_STAGES.find(function(s) { return s.status === status; });
    if (stage && !item[stage.dateField]) {
      patch[stage.dateField] = new Date().toISOString().slice(0, 10);
    }
    onPatch(patch);
  }

  // Date entry: record the date; auto-advance forward only.
  function setStageDate(stage, value) {
    var patch = {};
    patch[stage.dateField] = value;
    if (value && STATUSES.indexOf(stage.status) > statusIdx) {
      patch.status = stage.status;
    }
    onPatch(patch);
  }

  var stageOptions = PHYSICAL_STAGES.map(function(s) {
    return { value: s.status, dot: true, sub: s.sub };
  }).concat([{ value: 'Complete', dot: true, sub: 'installed + both ledgers settled' }]);
  // An item still at PO Issued has no physical stage yet — the dropdown
  // shows a placeholder until the first pick (or the receipt cascade).
  var dropdownValue = curStage >= 0 || item.status === 'Complete' ? item.status : '';

  var DATE_INP = {
    padding: '4px 7px', border: '0.5px solid #D8D7D2', borderRadius: 5,
    fontSize: 11.5, fontFamily: "'DM Sans', sans-serif", color: '#111111',
    outline: 'none', background: '#fff', width: 132, boxSizing: 'border-box',
  };
  var SMALL_INP = Object.assign({}, DATE_INP, { width: 148 });

  return (
    <div data-testid="tracking-timeline" style={{ marginBottom: 14 }}>
      {/* Where is it — one glanceable answer (derived, never typed) */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>Location</span>
        <span data-testid="tracking-location" style={{ fontSize: 13, fontWeight: 600, color: '#111111' }}>{location}</span>
        {estPretty && curStage < 3 && (
          <span style={{ fontSize: 11.5, color: '#888880' }}>(est. delivery {estPretty})</span>
        )}
      </div>

      {locked ? (
        <div style={{ fontSize: 12, color: '#111111', marginBottom: 10 }}>{item.status}</div>
      ) : (
        <div style={{ maxWidth: 300 }}>
          <Dropdown label={freeTrack ? 'Location' : 'Stage'} value={dropdownValue} onChange={pickStage}
            dataField="stage" placeholder={freeTrack ? 'Set location…' : 'Set stage…'} options={stageOptions} />
        </div>
      )}

      {/* The rail — every stage with its own date */}
      <div style={{ borderLeft: '1.5px solid #E0E0DC', marginLeft: 4, paddingLeft: 0 }}>
        {PHYSICAL_STAGES.map(function(stage, i) {
          var reached = curStage >= i || item.status === 'Complete';
          var isCurrent = curStage === i && item.status !== 'Complete';
          var dotColor = reached ? statusColor(stage.status) : '#FFFFFF';
          var dateVal = item[stage.dateField] || '';
          return (
            <div key={stage.status} data-testid={'stage-row-' + stage.status}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', position: 'relative' }}>
              {/* dot — filled when reached; the current stage gets a ring */}
              <span style={{
                width: 9, height: 9, borderRadius: '50%', marginLeft: -5.5, marginTop: 3,
                background: reached ? dotColor : '#FFFFFF',
                border: isCurrent ? '1.5px solid #111111' : '0.5px solid #D8D7D2',
                flex: 'none',
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 12, fontWeight: isCurrent ? 600 : 400,
                    color: reached ? '#111111' : '#888880', minWidth: 84,
                  }}>{stage.status}</span>
                  {locked ? (
                    <span style={{ fontSize: 11.5, color: dateVal ? '#111111' : '#BBBBBB' }}>{dateVal || '—'}</span>
                  ) : (
                    <input type="date" value={dateVal} data-field={stage.dateField}
                      onChange={function(e) { setStageDate(stage, e.target.value); }}
                      style={DATE_INP} />
                  )}
                  {stage.status === 'Shipped' && estPretty && !dateVal && (
                    <span style={{ fontSize: 10.5, color: '#888880' }}>(est. {estPretty})</span>
                  )}
                </div>
                {/* At Receiver extras — WHICH receiver (dropdown from the
                    directory's Receiver + Workroom vendors; binds the
                    item's ship-to, same field the Ships-to select edits) */}
                {stage.status === 'At Receiver' && !locked && (
                  <div style={{ marginTop: 5, maxWidth: 260 }}>
                    <Dropdown value={item.shipToVendorId != null ? String(item.shipToVendorId) : ''}
                      dataField="shipToVendorId" placeholder="Which receiver…"
                      onChange={function(v) {
                        onPatch({ shipToVendorId: v === '' ? null : (isNaN(Number(v)) ? v : parseInt(v, 10)) });
                      }}
                      options={[{ value: '', label: '—' }].concat(
                        receiverVendors.map(function(v) {
                          return { value: String(v.id), label: v.name + (v.vendorType === 'Workroom' ? ' (Workroom)' : '') };
                        }),
                        // Keep an off-taxonomy ship-to visible rather than
                        // showing a blank trigger for a real value.
                        shipToVendor && receiverVendors.indexOf(shipToVendor) === -1
                          ? [{ value: String(shipToVendor.id), label: shipToVendor.name }] : []
                      )} />
                  </div>
                )}
                {stage.status === 'At Receiver' && locked && shipToVendor && (
                  <div style={{ fontSize: 10.5, color: '#888880', marginTop: 3 }}>{shipToVendor.name}</div>
                )}
                {/* Shipped extras — tracking number + carrier */}
                {/* Text fields commit on BLUR — onPatch may write straight
                    to the DB (ItemForm commits saved items immediately),
                    and per-keystroke commits would spam saves + History. */}
                {stage.status === 'Shipped' && !locked && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
                    <input placeholder="Tracking #" defaultValue={item.trackingNumber || ''} data-field="trackingNumber"
                      autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                      onBlur={function(e) { if (e.target.value !== (item.trackingNumber || '')) onPatch({ trackingNumber: e.target.value }); }}
                      style={SMALL_INP} />
                    <input placeholder="Carrier" defaultValue={item.carrier || ''} data-field="carrier"
                      autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                      onBlur={function(e) { if (e.target.value !== (item.carrier || '')) onPatch({ carrier: e.target.value }); }}
                      style={SMALL_INP} />
                  </div>
                )}
                {stage.status === 'Shipped' && locked && (item.trackingNumber || item.carrier) && (
                  <div style={{ fontSize: 10.5, color: '#888880', marginTop: 3 }}>
                    {[item.trackingNumber, item.carrier].filter(Boolean).join(' · ')}
                  </div>
                )}
                {/* Installed extras — planned date(s): a named project
                    install event (dates derive live — moving the event on
                    the Project page moves every assigned item) or manual
                    date + number of days. */}
                {stage.status === 'Installed' && !locked && (
                  <div style={{ marginTop: 5 }}>
                    {/* Group date (project install event) OR an individual
                        date — side by side (Cimon 2026-07-30). An event
                        assignment wins; the manual inputs grey out while
                        one is picked. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, color: '#888880', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Planned</span>
                      <input type="date" value={item.installPlannedDate || ''} data-field="installPlannedDate"
                        disabled={!!item.installEventId}
                        onChange={function(e) { onPatch({ installPlannedDate: e.target.value }); }}
                        style={Object.assign({}, DATE_INP, item.installEventId ? { opacity: 0.4 } : null)} />
                      <span style={{ fontSize: 10, color: '#888880', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Days</span>
                      <input type="number" min="1" step="1" value={item.installDays || ''} data-field="installDays"
                        placeholder="1" disabled={!!item.installEventId}
                        onChange={function(e) { onPatch({ installDays: e.target.value }); }}
                        style={Object.assign({}, SMALL_INP, { width: 44 }, item.installEventId ? { opacity: 0.4 } : null)} />
                      {/* Dropdown ships a built-in 12px bottom margin (stacked-
                          form layouts) — neutralize it so the trigger sits on
                          the same line as the date + days inputs. */}
                      <div style={{ minWidth: 190, marginBottom: -12 }}>
                        <Dropdown value={item.installEventId || ''} dataField="installEventId"
                          placeholder="Install event…"
                          onChange={function(v) { onPatch({ installEventId: v }); }}
                          options={[{ value: '', label: installEvents.length > 0 ? 'Individual date' : 'None yet — add events on the Projects page' }].concat(
                            installEvents.map(function(ev) {
                              var evRange = plannedInstallDisplay({ installEventId: String(ev.id) }, p.project);
                              var evPast = installEventIsPast(ev, timelineToday) ? ' · past' : '';
                              return { value: String(ev.id), label: (ev.label || 'Install') + (evRange ? ' · ' + evRange : '') + evPast };
                            })
                          )} />
                      </div>
                    </div>
                    {item.installEventId && (
                      <div style={{ fontSize: 10.5, color: installPlanOverdue(item, p.project, timelineToday) ? '#665518' : '#888880', marginTop: 3 }}>
                        {plannedInstallDisplay(item, p.project) || 'Event has no dates yet — set them on the Projects page'}
                        {installPlanOverdue(item, p.project, timelineToday) ? ' — date passed, not yet installed' : ''}
                      </div>
                    )}
                  </div>
                )}
                {stage.status === 'Installed' && locked && plannedInstallDisplay(item, p.project) && (
                  <div style={{ fontSize: 10.5, color: installPlanOverdue(item, p.project, timelineToday) ? '#665518' : '#888880', marginTop: 3 }}>Planned {plannedInstallDisplay(item, p.project)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
